/**
 * Cal.com booking handler. Polls window.Cal.ns[namespace] up to 20 times
 * x 250ms. On linkReady -> posthog cal_scheduler_shown. On
 * bookingSuccessful -> use booking.uid as event_id, fire fbq Schedule
 * (if Meta attribution), dataLayer consultation_booked, posthog
 * consultation_booked, POST to /api/v1/forms/submit.
 */

import { captureAttribution, capLength, LENGTH_CAPS, hasMetaAttribution } from './attribution';
import { pushDataLayer } from './datalayer';
import { trackFbq } from './fbq';
import { getGA4ClientId } from './ga4';
import type { PixelConfig } from './config';
import { posthogCapture, posthogDistinctId, posthogIdentify, posthogSessionId } from './posthog';
import { sha256Hex } from './sha256';
import { uuidv4 } from './uuid';

interface CalApi {
  (action: string, options: Record<string, unknown>): void;
}

interface CalGlobal {
  ns?: Record<string, CalApi>;
}

declare global {
  interface Window {
    Cal?: CalGlobal & ((...args: unknown[]) => void);
  }
}

const POLL_ATTEMPTS = 20;
const POLL_INTERVAL_MS = 250;

export function installCalHandler(config: PixelConfig): void {
  const cal = config.cal;
  if (!cal || !cal.enabled) return;
  const namespace = cal.namespace;
  if (!namespace) return;
  let attempts = 0;
  const poll = (): void => {
    attempts++;
    const calGlobal = typeof window !== 'undefined' ? window.Cal : undefined;
    const ns = calGlobal && calGlobal.ns ? calGlobal.ns[namespace] : undefined;
    if (typeof ns === 'function') {
      attach(ns, config);
      return;
    }
    if (attempts < POLL_ATTEMPTS) setTimeout(poll, POLL_INTERVAL_MS);
  };
  poll();
}

function attach(cal: CalApi, config: PixelConfig): void {
  try {
    cal('on', {
      action: 'linkReady',
      callback: () => {
        posthogCapture('cal_scheduler_shown', { source: 'marketing_cta' });
      },
    });
  } catch {
    /* noop */
  }
  try {
    cal('on', {
      action: 'bookingSuccessful',
      callback: (ev: unknown) => {
        void handleBooking(ev, config);
      },
    });
  } catch {
    /* noop */
  }
}

interface CalBookingEvent {
  detail?: {
    data?: {
      booking?: { uid?: string; startTime?: string };
      eventType?: { title?: string };
      attendees?: Array<{ email?: string; name?: string }>;
    };
  };
}

async function handleBooking(ev: unknown, config: PixelConfig): Promise<void> {
  try {
    const data = (ev as CalBookingEvent)?.detail?.data || {};
    const booking = data.booking || {};
    const attendees = data.attendees || [];
    const primary = attendees[0] || {};
    const email = (primary.email || '').trim().toLowerCase();
    const eventId = (booking.uid && booking.uid.trim()) || uuidv4();
    const attribution = captureAttribution();
    const submitTime = Date.now();
    const timestampIso = new Date(submitTime).toISOString();
    const calFormId = (config.cal && config.cal.formId) || 'cal-booking';

    // 1. PostHog
    posthogCapture('consultation_booked', {
      source: 'marketing_cta',
      event_type: data.eventType?.title,
      booking_uid: booking.uid,
      start_time: booking.startTime,
      event_id: eventId,
    });
    if (email) {
      void sha256Hex(email).then((hash) => {
        if (hash) posthogIdentify(hash, { email_hash: hash });
      });
    }

    // 2. Meta Pixel (deduped via eventID)
    if (hasMetaAttribution()) {
      trackFbq(
        'Schedule',
        { content_name: 'consultation_booked', value: 500, currency: 'USD' },
        eventId,
        config.prodHosts
      );
    }

    // 3. dataLayer
    pushDataLayer({
      event: 'consultation_booked',
      event_id: eventId,
      form: calFormId,
      booking_uid: booking.uid,
      start_time: booking.startTime,
    });

    // 4. POST to /api/v1/forms/submit
    const name = (primary.name || '').trim();
    const parts = name.split(/\s+/).filter(Boolean);
    const firstName = parts.length > 1 ? parts.slice(0, -1).join(' ') : '';
    const lastName = parts.length > 1 ? parts[parts.length - 1] || '' : name;
    const ga4ClientId = config.ga4MeasurementId
      ? await safe(() => getGA4ClientId(config.ga4MeasurementId as string, 500))
      : undefined;
    await postBooking(config, calFormId, {
      email,
      firstName,
      lastName,
      eventTypeTitle: data.eventType?.title,
      bookingUid: booking.uid,
      startTime: booking.startTime,
      eventId,
      attribution,
      ga4ClientId,
      timestampIso,
    });
  } catch {
    /* booking flow best-effort */
  }
}

interface PostBookingArgs {
  email: string;
  firstName: string;
  lastName: string;
  eventTypeTitle: string | undefined;
  bookingUid: string | undefined;
  startTime: string | undefined;
  eventId: string;
  attribution: ReturnType<typeof captureAttribution>;
  ga4ClientId: string | undefined;
  timestampIso: string;
}

async function postBooking(
  config: PixelConfig,
  calFormId: string,
  args: PostBookingArgs
): Promise<void> {
  try {
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : undefined;
    const screenRes =
      typeof screen !== 'undefined' ? screen.width + 'x' + screen.height : undefined;
    const viewport =
      typeof window !== 'undefined' ? window.innerWidth + 'x' + window.innerHeight : undefined;
    const context = omitUndefined({
      event_id: capLength(args.eventId, LENGTH_CAPS.eventId),
      posthog_distinct_id: capLength(posthogDistinctId(), LENGTH_CAPS.posthogDistinctId),
      posthog_session_id: capLength(posthogSessionId(), LENGTH_CAPS.posthogSessionId),
      utm_source: args.attribution.utm.utm_source,
      utm_medium: args.attribution.utm.utm_medium,
      utm_campaign: args.attribution.utm.utm_campaign,
      utm_term: args.attribution.utm.utm_term,
      utm_content: args.attribution.utm.utm_content,
      fbc: args.attribution.fbc,
      fbp: args.attribution.fbp,
      gclid: args.attribution.gclid,
      ga4_client_id: capLength(args.ga4ClientId, LENGTH_CAPS.ga4ClientId),
      page_url: args.attribution.pageUrl,
      referrer: args.attribution.referrer,
      initial_referrer: capLength(args.attribution.initialReferrer, LENGTH_CAPS.initialReferrer),
      initial_search_engine: capLength(
        args.attribution.initialSearchEngine,
        LENGTH_CAPS.searchEngine
      ),
      initial_search_query: capLength(args.attribution.initialSearchQuery, LENGTH_CAPS.searchQuery),
      session_id: args.attribution.sessionId,
      landing_url: args.attribution.landingUrl,
      submission_timestamp: args.timestampIso,
      user_agent: userAgent,
      screen_resolution: screenRes,
      viewport_size: viewport,
      gbraid: args.attribution.gbraid,
      wbraid: args.attribution.wbraid,
      msclkid: args.attribution.msclkid,
      cal_booking_uid: args.bookingUid,
    });
    const data: Record<string, unknown> = omitUndefined({
      email: args.email,
      firstName: args.firstName,
      lastName: args.lastName,
      startTime: args.startTime,
      message:
        'Booked via marketing CTA. Event type: ' +
        (args.eventTypeTitle || 'unknown') +
        '. Booking UID: ' +
        (args.bookingUid || 'none') +
        '. Start: ' +
        (args.startTime || 'unknown'),
    });
    const url = config.apiBaseUrl.replace(/\/+$/, '') + '/api/v1/forms/submit';
    await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ form: calFormId, data, context }),
      keepalive: true,
    });
  } catch {
    /* best-effort */
  }
}

function omitUndefined(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined || v === null || v === '') continue;
    out[k] = v;
  }
  return out;
}

async function safe<T>(fn: () => Promise<T> | T): Promise<T | undefined> {
  try {
    return await fn();
  } catch {
    return undefined;
  }
}
